<div class="tlr-page">
  <app-project-nav></app-project-nav>

  <app-page-header [title]="'testRunner.title' | translate" [subtitle]="subtitle()">
    <ng-container titleAside>
      @if (state(); as runnerState) {
        @if (runnerState.runKey) {
          <app-code-id-chip [id]="runnerState.runKey" />
        }
        <span
          class="tlr-execution"
          data-testid="runner-execution-nr"
          [attr.aria-label]="
            startedAtLabel()
              ? ('testRunner.executionNrAt' | translate: { nr: runnerState.nr, at: startedAtLabel() })
              : null
          "
        >
          <span class="tlr-execution__dot" aria-hidden="true"></span>
          {{ 'testRunner.executionNr' | translate: { nr: runnerState.nr } }}
          @if (startedAtLabel(); as at) {
            <span class="tlr-execution__at" data-testid="runner-execution-started-at">· {{ at }}</span>
          }
        </span>
        <app-art-pill [art]="runnerState.runKind" />
      }
    </ng-container>
    <ng-container right>
      @if (pauseOrHandoverOffered() && !paused()) {
        <button
          type="button"
          class="tlr-btn tlr-btn--ghost"
          data-testid="testrun-pause-btn"
          [disabled]="!canPause()"
          (click)="pause()"
        >
          <mat-icon aria-hidden="true">pause</mat-icon>
          {{ 'testRunner.pause.action' | translate }}
        </button>
      }
      @if (pauseOrHandoverOffered()) {
        <ng-container
          *ngTemplateOutlet="
            handoverButton;
            context: { testid: 'testrun-handover-btn', labelKey: 'testRunner.handover.action' }
          "
        />
      }
      @if (abortOffered()) {
        <button
          type="button"
          class="tlr-btn tlr-btn--ghost"
          data-testid="testlauf-abbrechen"
          [attr.aria-label]="'testRunner.abortAria' | translate"
          (click)="openAbort()"
        >
          <mat-icon aria-hidden="true">stop_circle</mat-icon>
          {{ 'testRunner.abortAction' | translate }}
        </button>
      }
      @if (showResultsOffered()) {
        <button type="button" class="tlr-btn tlr-btn--ghost" data-testid="runner-open-results" (click)="goToResults()">
          <mat-icon aria-hidden="true">fact_check</mat-icon>
          {{ 'testRunner.showResults' | translate }}
        </button>
      }
      <button type="button" class="tlr-btn tlr-btn--ghost" data-testid="runner-to-overview" (click)="goToOverview()">
        <mat-icon aria-hidden="true">chevron_left</mat-icon>
        {{ 'testRunner.toOverview' | translate }}
      </button>
    </ng-container>
  </app-page-header>

  @if (loading()) {
    <div
      class="tlr-loading"
      role="status"
      data-testid="runner-loading"
      [attr.aria-label]="'testRunner.loadingAria' | translate"
    >
      <mat-spinner diameter="32" />
    </div>
  } @else if (loadError()) {
    <p class="tlr-error" role="alert" data-testid="runner-error">{{ 'testRunner.loadError' | translate }}</p>
  } @else if (state(); as runnerState) {
    @if (!automated() && !finished()) {
      <section class="tlr-progress" [attr.aria-label]="'testRunner.progressAria' | translate">
        <div class="tlr-progress__row">
          <span class="tlr-progress__counts" data-testid="runner-progress-counts">
            <strong>{{ passedOfTotal() }}</strong>
            {{ 'testRunner.passedOfTotal' | translate }} ·
            {{ 'testRunner.percentDone' | translate: { percent: progress() } }}
          </span>
          <span class="tlr-progress__spacer"></span>
          <span class="tlr-progress__status" data-testid="runner-overall-status">
            <span class="tlr-progress__status-label">{{ 'testRunner.overallStatus' | translate }}</span>
            <app-res-pill
              [status]="pillStatus(runnerState.status)"
              [label]="resultKey(runnerState.status) | translate"
              [pulsing]="runnerState.live"
            />
          </span>
        </div>
        <div
          class="tlr-progress__bar"
          role="progressbar"
          data-testid="runner-progress-bar"
          aria-valuemin="0"
          aria-valuemax="100"
          [attr.aria-valuenow]="progress()"
          [attr.aria-label]="'testRunner.progressBarAria' | translate"
        >
          <span class="tlr-progress__fill" [style.width.%]="progress()"></span>
        </div>
      </section>
    }

    @if (pausedOnly()) {
      <section
        class="tlr-banner tlr-banner--paused"
        role="status"
        data-testid="testrun-paused-banner"
        [attr.aria-label]="'testRunner.pause.bannerAria' | translate"
      >
        <span class="tlr-banner__tag">{{ 'testRunner.pause.bannerLabel' | translate }}</span>
        <span class="tlr-banner__text">
          @if (pausedAtStep()) {
            {{ 'testRunner.pause.bannerText' | translate: { step: pausedAtStep() } }}
          } @else {
            {{ 'testRunner.pause.bannerTextNoStep' | translate }}
          }
        </span>
        <span class="tlr-banner__spacer"></span>
        @if (pauseOrHandoverOffered()) {
          <ng-container
            *ngTemplateOutlet="
              handoverButton;
              context: { testid: 'testrun-banner-handover-btn', labelKey: 'testRunner.handover.fromBanner' }
            "
          />
        }
        @if (resumeOffered()) {
          <button
            type="button"
            class="tlr-btn tlr-btn--primary"
            data-testid="testrun-resume-btn"
            [disabled]="!canResume()"
            (click)="resume()"
          >
            <mat-icon aria-hidden="true">play_arrow</mat-icon>
            {{ 'testRunner.pause.resume' | translate }}
          </button>
        }
      </section>
    }

    @if (handedOver()) {
      <section
        class="tlr-banner tlr-banner--handover"
        role="status"
        data-testid="testrun-handover-banner"
        [attr.aria-label]="'testRunner.handover.bannerAria' | translate"
      >
        <span class="tlr-banner__tag">{{ 'testRunner.handover.bannerLabel' | translate }}</span>
        <div class="tlr-banner__recipient">
          <app-avatar [name]="recipientName()" [size]="28" />
          <div class="tlr-banner__recipient-text">
            <p class="tlr-banner__recipient-name">{{ recipientName() }}</p>
            <p class="tlr-banner__recipient-meta">
              {{ recipientKindKey() | translate }}
              @if (handoverNote()) {
                <span>&nbsp;·&nbsp;{{ handoverNote() }}</span>
              }
            </p>
          </div>
        </div>
        <span class="tlr-banner__spacer"></span>
        @if (pausedAtStep()) {
          <span class="tlr-banner__text">
            {{ 'testRunner.handover.savedAtStep' | translate: { step: pausedAtStep() } }}
          </span>
        }
        @if (canTakeOver()) {
          <button
            type="button"
            class="tlr-btn tlr-btn--primary"
            data-testid="testrun-take-over-btn"
            (click)="takeOver()"
          >
            <mat-icon aria-hidden="true">play_arrow</mat-icon>
            {{ 'testRunner.handover.takeOver' | translate }}
          </button>
        }
      </section>
    }

    @if (reloading()) {
      <p class="tlr-reloading" role="status" data-testid="runner-reloading">
        {{ 'testRunner.reloading' | translate }}
      </p>
    }

    <ng-template #comments>
      <app-test-run-comments
        [runId]="runId"
        [executionId]="runnerState.executionId"
        [currentUserId]="currentUserId()"
        [canComment]="canComment()"
        [stepReferences]="commentStepReferences()"
      />
    </ng-template>

    @if (!automated()) {
      <ng-container [ngTemplateOutlet]="comments" />
    }

    @if (finished() && !automated()) {
      <app-run-summary-card
        [status]="summaryStatus()"
        [statusLabel]="resultKey(summaryStatus()) | translate"
        [passed]="runnerState.stepsPassed"
        [total]="runnerState.stepsTotal"
        [failed]="summaryFailed()"
        [blocked]="summaryBlocked()"
        [skipped]="summarySkipped()"
        [duration]="summaryDuration()"
        [resultsAvailable]="true"
        [aborted]="aborted()"
        [abortReason]="abortReason()"
        [focusOnShow]="focusSummary()"
        (leave)="goToExecutions()"
        (showResults)="goToResults()"
      />
    } @else if (automated()) {
      <app-testlauf-runner-live
        [projectId]="projectId"
        [runId]="runId"
        [state]="runnerState"
        [source]="runSource()"
        (pipelineCompleted)="goToExecutions()"
        (staleState)="onStaleState()"
      />
      <ng-container [ngTemplateOutlet]="comments" />
    } @else {
      <app-testlauf-runner-manuell
        [projectId]="projectId"
        [runId]="runId"
        [state]="runnerState"
        [completing]="completing()"
        [revealMissingActual]="revealMissingActual()"
        (staleState)="onStaleState()"
        (stateChanged)="onStateChanged($event)"
        (unsavedInput)="onUnsavedInputChange($event)"
        (completeRequested)="complete()"
      />
    }
  }
</div>

<app-test-run-handover-modal
  [visible]="handoverOpen()"
  [people]="handoverPeople()"
  [stepPosition]="pausedAtStep()"
  [submitting]="handoverSubmitting()"
  (confirmed)="submitHandover($event)"
  (cancelled)="closeHandover()"
/>

<app-test-run-abort-dialog
  [visible]="abortOpen()"
  [submitting]="abortSubmitting()"
  [unsavedInput]="hasUnsavedInput()"
  [errorKey]="abortErrorKey()"
  (confirmed)="submitAbort($event)"
  (cancelled)="closeAbort()"
/>

<lib-modal
  [title]="'common.unsavedChanges.title' | translate"
  [text]="'testRunner.confirmLeave' | translate"
  [visible]="showDeactivateConfirm()"
  [saveText]="'common.unsavedChanges.leave' | translate"
  [cancelText]="'common.unsavedChanges.stay' | translate"
  (save)="onDeactivateConfirmed()"
  (cancel)="onDeactivateCancelled()"
  (visibleChange)="!$event && onDeactivateCancelled()"
></lib-modal>

<ng-template #handoverButton let-testid="testid" let-labelKey="labelKey">
  <button
    type="button"
    class="tlr-btn tlr-btn--ghost"
    [attr.data-testid]="testid"
    [disabled]="!canHandOver()"
    (click)="openHandover()"
  >
    <mat-icon aria-hidden="true">reply</mat-icon>
    {{ labelKey | translate }}
  </button>
</ng-template>
